import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, CheckCircle2, Loader2 } from 'lucide-react';
import { careerPositions, licenseTypes, workTypes, careersFaqs } from '../mock';
import { PageFaq } from '../sections/RestSections';
import { useAntiBot, Honeypot } from '../components/useAntiBot';
import TurnstileWidget, { TURNSTILE_ENABLED } from '../components/TurnstileWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PositionGrid({ active, onChoose }) {
  return (
    <div className="pos-grid">
      {careerPositions.map((p) => (
        <div className={`pos-card ${active === p.id ? 'active' : ''}`} key={p.id}>
          <h3>{p.title}</h3>
          <p>{p.blurb}</p>
          <button className="btn btn-gold" onClick={() => onChoose(p.id)}>Apply for {p.title}</button>
        </div>
      ))}
    </div>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button type="button" key={o.val} className={value === o.val ? 'on' : ''} onClick={() => onChange(o.val)}>{o.label}</button>
      ))}
    </div>
  );
}

function LicenseChips({ show, value, onChange }) {
  if (!show) return null;
  return (
    <div className="fld-row">
      <span className="fld-label">License type</span>
      <div className="chip-row">
        {licenseTypes.map((lt) => (
          <button type="button" key={lt} className={`chip ${value === lt ? 'on' : ''}`} onClick={() => onChange(lt)}>{lt}</button>
        ))}
      </div>
    </div>
  );
}

function ApplicationForm({
  position, setPosition, status, setStatus, licenseType, setLicenseType,
  workType, setWorkType, form, setForm, resume, setResume, state, setState, onSubmit,
  hp, setHp, setToken,
}) {
  if (state.done) {
    return (
      <div className="apply-done">
        <CheckCircle2 size={40} />
        <h3>Application received!</h3>
        <p>Thanks for applying to Victory Plumbing. We&rsquo;ll review your application and be in touch shortly.</p>
        <button className="btn btn-outline-dark" onClick={() => setState({ loading: false, done: false, error: '' })}>Submit another</button>
      </div>
    );
  }
  const activeTitle = careerPositions.find((x) => x.id === position)?.title;
  return (
    <form onSubmit={onSubmit}>
      <Honeypot value={hp} onChange={setHp} />
      <h3>Application {position ? `\u2014 ${activeTitle}` : ''}</h3>

      <div className="fld-row">
        <label>Position <b>*</b>
          <select value={position} onChange={(e) => setPosition(e.target.value)} required>
            <option value="" disabled>Select a position</option>
            {careerPositions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </label>
      </div>

      <div className="fld-row two">
        <label>Full Name <b>*</b><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
        <label>Phone <b>*</b><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></label>
      </div>
      <div className="fld-row">
        <label>Email <b>*</b><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></label>
      </div>

      <div className="fld-row">
        <span className="fld-label">Are you a licensed plumber?</span>
        <Segmented
          options={[{ val: 'unlicensed', label: 'No' }, { val: 'licensed', label: 'Yes' }]}
          value={status}
          onChange={setStatus}
        />
      </div>

      <LicenseChips show={status === 'licensed'} value={licenseType} onChange={setLicenseType} />

      <div className="fld-row">
        <span className="fld-label">Work preference</span>
        <Segmented options={workTypes.map((w) => ({ val: w, label: w }))} value={workType} onChange={setWorkType} />
      </div>

      <div className="fld-row">
        <label>Tell Us About Yourself (optional)<textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></label>
      </div>

      <div className="fld-row">
        <span className="fld-label">Resume (PDF or Word) <b>*</b></span>
        <label className={`file-drop ${resume ? 'has-file' : ''}`}>
          <Upload size={18} />
          <span>{resume ? resume.name : 'Choose file to upload'}</span>
          <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResume(e.target.files[0])} hidden />
        </label>
      </div>

      {state.error && <div className="apply-error">{state.error}</div>}

      <TurnstileWidget onToken={setToken} />
      <button className="btn btn-gold apply-submit" type="submit" disabled={state.loading}>
        {state.loading ? <><Loader2 size={16} className="spin" /> Submitting...</> : 'Submit Application'}
      </button>
    </form>
  );
}

export default function CareersPage() {
  const [position, setPosition] = useState('');
  const [status, setStatus] = useState('unlicensed');
  const [licenseType, setLicenseType] = useState(licenseTypes[0]);
  const [workType, setWorkType] = useState(workTypes[0]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [resume, setResume] = useState(null);
  const [state, setState] = useState({ loading: false, done: false, error: '' });
  const { hp, setHp, appendAntiBot } = useAntiBot();
  const [token, setToken] = useState('');
  const formRef = useRef(null);

  const choose = (id) => {
    setPosition(id);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!resume) {
      setState({ loading: false, done: false, error: 'Please attach your resume (PDF or Word) before submitting.' });
      return;
    }
    if (TURNSTILE_ENABLED && !token) {
      setState({ loading: false, done: false, error: 'Please complete the "Verify you are human" check before submitting.' });
      return;
    }
    setState({ loading: true, done: false, error: '' });
    try {
      const fd = new FormData();
      fd.append('position', position || 'plumber');
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('status', status);
      fd.append('license_type', status === 'licensed' ? licenseType : '');
      fd.append('work_type', workType);
      fd.append('message', form.message);
      if (resume) fd.append('resume', resume);
      appendAntiBot(fd);
      if (token) fd.append('turnstile_token', token);
      await axios.post(`${API}/careers/apply`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setState({ loading: false, done: true, error: '' });
      setForm({ name: '', email: '', phone: '', message: '' });
      setResume(null);
    } catch (err) {
      setState({ loading: false, done: false, error: 'Something went wrong. Please call us at (912) 429-9428.' });
    }
  };

  return (
    <main className="page careers-page">
      <section className="page-hero">
        <div className="wrap">
          <p className="eyebrow">We&rsquo;re Hiring</p>
          <h1>Build your career with Victory Plumbing</h1>
          <p className="lede">Family-owned and growing. Pick a position below, tell us about yourself, attach your resume, and apply in minutes.</p>
        </div>
      </section>

      <section className="careers-body">
        <div className="wrap">
          <PositionGrid active={position} onChoose={choose} />
          <div className="apply-form" ref={formRef}>
            <ApplicationForm
              position={position} setPosition={setPosition}
              status={status} setStatus={setStatus}
              licenseType={licenseType} setLicenseType={setLicenseType}
              workType={workType} setWorkType={setWorkType}
              form={form} setForm={setForm}
              resume={resume} setResume={setResume}
              state={state} setState={setState}
              onSubmit={submit}
              hp={hp} setHp={setHp} setToken={setToken}
            />
          </div>
        </div>
      </section>
      <PageFaq title="Careers FAQs" subtitle="What to know before you apply." items={careersFaqs} testid="careers-faqs" />
    </main>
  );
}
